import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material'
import { QrCode2, Edit, Send, Cancel, SkipNext, Close } from '@mui/icons-material'
import { Scanner, IDetectedBarcode } from '@yudiel/react-qr-scanner'
import { SessionNodeDto, PendingInputDto } from '@api/types/session'
import { useSessionInput } from '../../../hooks/useSessionInput'

// Extract code from URL - handles URLs like https://example.com/?CODE or plain codes
const extractCodeFromUrl = (rawValue: string): string => {
  try {
    // Check if it's a URL
    if (rawValue.includes('://') || rawValue.startsWith('www.')) {
      const url = new URL(rawValue.startsWith('www.') ? `https://${rawValue}` : rawValue)
      // Get everything after the ? (the search string without the leading ?)
      if (url.search) {
        return url.search.substring(1) // Remove the leading ?
      }
      // Fallback: check pathname for code
      const pathParts = url.pathname.split('/').filter(Boolean)
      if (pathParts.length > 0) {
        return pathParts[pathParts.length - 1]
      }
    }
  } catch {
    // Not a valid URL, treat as plain code
  }
  return rawValue
}

interface QrScannerInputProps {
  node: SessionNodeDto
  pendingInput: PendingInputDto
  sessionId: string | null
  onSubmitSuccess?: () => void
  onClose?: () => void
}

export default function QrScannerInput({
  node,
  pendingInput,
  sessionId,
  onSubmitSuccess,
  onClose,
}: QrScannerInputProps) {
  const [tabIndex, setTabIndex] = useState(0)
  const [manualCode, setManualCode] = useState('')
  const [scanError, setScanError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const hasCalledSuccessRef = useRef(false)

  const { submitQrCode, skipChallenge, cancelChallenge, isSubmitting, isSuccess } = useSessionInput(sessionId)

  // Close dialog after successful submission
  useEffect(() => {
    if (isSuccess && onSubmitSuccess && !hasCalledSuccessRef.current) {
      hasCalledSuccessRef.current = true
      onSubmitSuccess()
    }
  }, [isSuccess, onSubmitSuccess])

  const handleScan = useCallback((detectedCodes: IDetectedBarcode[]) => {
    if (isProcessing || isSubmitting || detectedCodes.length === 0) return

    const rawValue = detectedCodes[0].rawValue
    if (!rawValue || !node.id) return

    // Extract the actual code from the URL
    const code = extractCodeFromUrl(rawValue)
    if (!code) return

    setIsProcessing(true)
    setScanError(null)
    submitQrCode(node.id, code)
  }, [isProcessing, isSubmitting, node.id, submitQrCode])

  const handleCameraError = useCallback((error: unknown) => {
    console.error('Camera error:', error)
    if (error instanceof Error) {
      if (error.name === 'NotAllowedError') {
        setScanError('Camera access was denied. Please use manual entry.')
      } else if (error.name === 'NotFoundError') {
        setScanError('No camera found. Please use manual entry.')
      } else {
        setScanError('Unable to access camera. Please use manual entry.')
      }
    } else {
      setScanError('Unable to access camera. Please use manual entry.')
    }
    // Switch to manual tab on camera error
    setTabIndex(1)
  }, [])

  const handleManualSubmit = () => {
    const trimmed = manualCode.trim()
    if (trimmed && node.id) {
      submitQrCode(node.id, trimmed)
    }
  }

  const handleSkip = () => {
    if (!node.id || !pendingInput.allowSkip) return
    skipChallenge(node.id, pendingInput.inputType)
  }

  const handleCancel = () => {
    if (!node.id || !pendingInput.allowCancel) return
    cancelChallenge(node.id, pendingInput.inputType)
  }

  return (
    <Card sx={{ maxWidth: 500, mx: 'auto', position: 'relative' }}>
      {onClose && (
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
        >
          <Close fontSize="small" />
        </IconButton>
      )}
      <CardContent>
        <Typography variant="h5" gutterBottom textAlign="center">
          {node.label || 'Scan QR Code'}
        </Typography>
        {node.description && (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
            {node.description}
          </Typography>
        )}

        {/* Tab selector */}
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          centered
          sx={{ mb: 2 }}
        >
          <Tab icon={<QrCode2 />} label="Scan" />
          <Tab icon={<Edit />} label="Manual" />
        </Tabs>

        {/* Scan tab */}
        {tabIndex === 0 && (
          <Box>
            {scanError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {scanError}
              </Alert>
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 300,
                  bgcolor: 'black',
                  borderRadius: 1,
                  overflow: 'hidden',
                  mb: 2,
                }}
              >
                <Scanner
                  onScan={handleScan}
                  onError={handleCameraError}
                  constraints={{
                    facingMode: 'environment',
                  }}
                  sound={false}
                  styles={{
                    container: {
                      width: '100%',
                      height: '100%',
                    },
                    video: {
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    },
                  }}
                />
                {/* Processing overlay */}
                {(isProcessing || isSubmitting) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      zIndex: 20,
                    }}
                  >
                    <CircularProgress color="inherit" size={48} sx={{ mb: 2 }} />
                    <Typography>Validating code...</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Manual entry tab */}
        {tabIndex === 1 && (
          <Box>
            <TextField
              fullWidth
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="Enter code manually"
              disabled={isSubmitting}
              sx={{ mb: 2 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && manualCode.trim()) {
                  handleManualSubmit()
                }
              }}
            />
          </Box>
        )}

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          {pendingInput.allowCancel && (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<Cancel />}
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              {pendingInput.cancelButtonText || 'Cancel'}
            </Button>
          )}
          {pendingInput.allowSkip && (
            <Button
              variant="outlined"
              startIcon={<SkipNext />}
              onClick={handleSkip}
              disabled={isSubmitting}
            >
              {pendingInput.skipButtonText || 'Skip'}
            </Button>
          )}
          {tabIndex === 1 && (
            <Button
              variant="contained"
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
              onClick={handleManualSubmit}
              disabled={!manualCode.trim() || isSubmitting}
            >
              Submit
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}
